import { CSSProperties, useEffect, useMemo, useState } from "react";
import {
  isExternalGenericLink,
  isProductSpecificType,
  getThumbnailImageUrl,
} from "@markit/common.utils";
import { Event, EventType, MediaAspectRatio } from "@markit/common.types";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import { getProductDetails } from "../../../utils/productUtils";
import { useTheme } from "../../../hooks/useTheme";

type EventItemImageProps = {
  item: Event | undefined;
  size: number;
  aspectRatio?: MediaAspectRatio;
  containerStyles?: CSSProperties;
  altImageIcon?: string; // ie. ribbon
  scaleImage?: boolean; // true if we want to scale the image down to fit the constraints while showing the full image
};

// Displays the event image if mediaURLs is present, otherwise displays event type icon
const EventItemImage = ({
  item,
  size,
  aspectRatio,
  containerStyles,
  altImageIcon,
  scaleImage = false,
}: EventItemImageProps) => {
  const { theme } = useTheme();

  const styles = {
    imagePreview: {
      height: size,
      width: size,
      borderRadius: 8,
      cursor: "pointer",
      border: `0.5px solid ${Colors.GRAY2}`,
      boxSizing: "border-box" as const,
      ...containerStyles,
    },
    // Container for scaled image (scaleImage = true)
    imageContainerScaled: {
      height: size,
      width: size,
      borderRadius: 8,
      boxSizing: "border-box" as const,
      overflow: "hidden",
      cursor: "pointer",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      ...containerStyles,
    },
    // Container for cropped image (scaleImage = false)
    imageContainerCropped: {
      height: size,
      width: size,
      borderRadius: 8,
      boxSizing: "border-box" as const,
      overflow: "hidden",
      cursor: "pointer",
      ...containerStyles,
    },
    imageWrapper: {
      width: size,
      height: size,
      maxWidth: size,
      maxHeight: size,
      borderRadius: 8,
      border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      boxSizing: "border-box" as const,
      overflow: "hidden",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    // Image styles for scaled approach (scaleImage = true)
    imageScaled: {
      maxWidth: size,
      maxHeight: size,
      width: "auto",
      height: "auto",
      display: "block",
      objectFit: "contain" as const,
    },
    // Image styles for cropped approach (scaleImage = false)
    imageCropped: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as const,
      borderRadius: 8,
      border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      boxSizing: "border-box" as const,
      display: "block",
    },
  };

  const thumbnailImage = useMemo(() => getThumbnailImageUrl(item), [item]);

  // Some scraped events (e.g. Showclix/Eventbrite) have a thumbnail URL that's present but
  // dead - track actual load failures so those fall back to the icon instead of a broken <img>
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [thumbnailImage]);
  const showThumbnail = !!thumbnailImage && !imageFailed;

  // Displays the main image container with the correct aspect ratio instead of a square border
  const useCssAspectRatio = useMemo(
    () => aspectRatio != null && aspectRatio !== MediaAspectRatio.NONE,
    [aspectRatio]
  );

  return (
    <div>
      {item ? (
        showThumbnail ? (
          scaleImage ? (
            <div
              style={
                useCssAspectRatio
                  ? {
                      ...styles.imageContainerScaled,
                      width: "fit-content",
                      height: "fit-content",
                      maxWidth: size,
                    }
                  : styles.imageContainerScaled
              }
            >
              <div
                style={
                  useCssAspectRatio
                    ? {
                        ...styles.imageWrapper,
                        width: "auto",
                        height: "auto",
                        maxWidth: size,
                        maxHeight: size,
                        aspectRatio: aspectRatio,
                      }
                    : styles.imageWrapper
                }
              >
                <img
                  alt="RenderedEvent"
                  src={thumbnailImage}
                  onError={() => setImageFailed(true)}
                  style={
                    useCssAspectRatio
                      ? {
                          ...styles.imageScaled,
                          width: "100%",
                          height: "100%",
                          maxWidth: "100%",
                          maxHeight: "100%",
                        }
                      : styles.imageScaled
                  }
                />
              </div>
            </div>
          ) : (
            <div style={styles.imageContainerCropped}>
              <img
                alt="RenderedEvent"
                src={thumbnailImage}
                onError={() => setImageFailed(true)}
                style={styles.imageCropped}
              />
            </div>
          )
        ) : isExternalGenericLink(item.eventType) ||
          isProductSpecificType(item.eventType) ? (
          <div className="Centering" style={styles.imagePreview}>
            <Icon
              icon={
                altImageIcon ||
                (item.eventType === EventType.POST
                  ? "ion:newspaper"
                  : isProductSpecificType(item.eventType)
                    ? getProductDetails(item.eventType).icon
                    : item.eventType === EventType.GENERIC_LINK_NO_DATA
                      ? "mdi:link-variant"
                      : "ion:document-text")
              }
              height={Math.round(size * 0.55)}
              color={
                isProductSpecificType(item.eventType)
                  ? Colors.GRAY1
                  : Colors.BLACK
              }
            />
          </div>
        ) : (
          // Remaining types here are all event-related (MARKIT, WEBINAR, linked events, etc.)
          <div className="Centering" style={styles.imagePreview}>
            <Icon
              icon="ion:calendar"
              height={Math.round(size * 0.55)}
              style={{ color: Colors.GRAY1 }}
            />
          </div>
        )
      ) : (
        <div className="Centering" style={styles.imagePreview}>
          <Icon
            icon="ion:link"
            height={Math.round(size * 0.65)}
            style={{ color: Colors.GRAY1 }}
          />
        </div>
      )}
    </div>
  );
};

export default EventItemImage;
